import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {isOk} from '../../../../utils/apiResponse';
import {fetchAgencyRules, isForbidden, saveAgencyRule} from '../../../ga4_attribution/ga4AttributionApi';
import {
    agencyRulesFromPayload, isDirty, rowIsEditable, ruleFromPayload, savedRulesByChannel, withField,
} from '../../../ga4_attribution/attributionModel';

/**
 * Las reglas de cruce de la agencia (`GET/PUT agency/ga4-attribution-rules`, `contrato.md` § 2): lo
 * guardado, el borrador de cada plataforma y el guardado.
 *
 * `status`: `loading` → `ready`, o `absent` (404: backend viejo; el bloque no se dibuja) o `error`.
 *
 * Se guarda de a UNA plataforma (UD-10: cada línea del bloque tiene su «Guardar», y «Usar esta regla»
 * guarda la sugerencia sin pasar por los selects). El PUT responde el payload entero: se toma como lo
 * guardado, y el borrador de ESA plataforma pasa a ser lo que guardó el backend. Los borradores de las
 * demás no se tocan.
 */
export function useGa4AttributionRules() {
    const [status, setStatus] = useState('loading');
    const [server, setServer] = useState(null);
    const [draft, setDraft] = useState({});
    const [canEdit, setCanEdit] = useState(true);
    const [savingChannel, setSavingChannel] = useState(null);
    const [attempt, setAttempt] = useState(0);

    // Lo guardado ANTES del último payload: el refresco decide con eso qué filas estaban sucias.
    const serverRef = useRef(null);
    // El guardado en curso, para no mandar dos PUT a la vez (dos clics antes del re-render).
    const savingRef = useRef(false);

    /**
     * Toma un payload como lo guardado. Con `keepDirtyDrafts` (el refresco de fondo) una fila conserva su
     * borrador sólo si el usuario la había cambiado respecto de lo guardado ANTERIOR: comparar contra lo
     * nuevo marcaría como cambiada una fila intacta que otro administrador tocó mientras tanto, y un
     * «Guardar» posterior le revertiría ese cambio (review C-regla-frontend-04).
     */
    const applyServer = useCallback((payload, {keepDirtyDrafts = false} = {}) => {
        const next = agencyRulesFromPayload(payload);
        const nextSaved = savedRulesByChannel(next.rules);
        const prevSaved = savedRulesByChannel(serverRef.current ? serverRef.current.rules : []);
        serverRef.current = next;
        setServer(next);
        setCanEdit(next.canEdit);
        setDraft((current) => {
            if (!keepDirtyDrafts) return nextSaved;
            const merged = {...nextSaved};
            Object.keys(current).forEach((channel) => {
                if (nextSaved[channel] && isDirty(prevSaved[channel], current[channel])) merged[channel] = current[channel];
            });
            return merged;
        });
    }, []);

    useEffect(() => {
        let cancelled = false;
        setStatus('loading');
        fetchAgencyRules().then((response) => {
            if (cancelled) return;
            if (response && response.absent) {
                setStatus('absent');
                return;
            }
            if (!isOk(response) || !response.data) {
                setStatus('error');
                return;
            }
            applyServer(response.data);
            setStatus('ready');
        });
        return () => { cancelled = true; };
    }, [attempt, applyServer]);

    /**
     * Vuelve a pedir sin pasar por el loader ni tocar lo que el usuario está editando: después de
     * guardar la regla propia de una cuenta, la lista de «Cuentas con su propia regla» puede cambiar.
     * Si falla, queda lo que había (es un refresco de fondo).
     */
    const refresh = useCallback(() => {
        fetchAgencyRules().then((response) => {
            if (isOk(response) && response.data) applyServer(response.data, {keepDirtyDrafts: true});
        });
    }, [applyServer]);

    const rules = useMemo(() => (server ? server.rules : []), [server]);
    const saved = useMemo(() => savedRulesByChannel(rules), [rules]);
    const dirtyChannels = useMemo(() => rules
        .filter((rule) => rowIsEditable(rule, canEdit) && isDirty(saved[rule.channel], draft[rule.channel]))
        .map((rule) => rule.channel), [rules, saved, draft, canEdit]);

    const setField = useCallback((channel, field, value) => {
        setDraft((current) => ({...current, [channel]: withField(current[channel], field, value)}));
    }, []);

    /** Descarta el borrador de una plataforma: vuelve a lo guardado («Cambiar» y «Cancelar»). */
    const resetDraft = useCallback((channel) => {
        const current = serverRef.current ? savedRulesByChannel(serverRef.current.rules) : {};
        setDraft((drafts) => ({...drafts, [channel]: current[channel] || ruleFromPayload(null)}));
    }, []);

    /**
     * Guarda la regla de UNA plataforma: la que se pasa (la sugerencia) o, sin ella, su borrador.
     * `{ok}`, o `{ok: false, forbidden}` con el 403: el bloque pasa a solo lectura.
     */
    const saveChannel = useCallback(async (channel, rule) => {
        if (savingRef.current) return {ok: false, busy: true};
        const body = ruleFromPayload(rule || draft[channel]);
        savingRef.current = true;
        setSavingChannel(channel);
        try {
            const response = await saveAgencyRule(channel, body);
            if (isForbidden(response)) {
                setCanEdit(false);
                return {ok: false, forbidden: true};
            }
            if (!isOk(response) || !response.data) return {ok: false};
            const next = agencyRulesFromPayload(response.data);
            const nextSaved = savedRulesByChannel(next.rules);
            serverRef.current = next;
            setServer(next);
            setDraft((current) => ({...current, [channel]: nextSaved[channel] || body}));
            return {ok: true};
        } finally {
            savingRef.current = false;
            setSavingChannel(null);
        }
    }, [draft]);

    return {
        status,
        hasGa4Connection: Boolean(server && server.hasGa4Connection),
        rules,
        suggestions: server ? server.suggestions : [],
        overrides: server ? server.overrides : [],
        saved,
        draft,
        setField,
        resetDraft,
        isRowDirty: (channel) => dirtyChannels.includes(channel),
        canEdit,
        saveChannel,
        savingChannel,
        refresh,
        retry: () => setAttempt((n) => n + 1),
    };
}

export default useGa4AttributionRules;
